import { mdiAlertCircleOutline } from '@mdi/js'
import { Icon } from '@mdi/react'
import TextEditor, { CaptionPlaceholder } from 'components/TextEditor'
import { useElementSize } from 'hooks/useElementSize'
import { ARTICLE_TYPE, PHOTO_TYPE } from 'interfaces'
import { Fragment, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { from, Subject } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { getLinkDetails, setComposerStatus } from 'services/compose'
import {
  composerContentItemSelector,
  composerImageUrlsSelector,
  composerLinkSelector,
  composerResetKeySelector,
  composerVideoUrlSelector,
  genericPostSelector,
  multiPostsCountSelector,
  visibleMultiPostsSelector
} from 'services/compose/selectors'
import { COMPOSER_STATUS_KEY_GENERIC } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { extractUrlsFromText } from 'utils/composer'
import Accordion from '../Accordion'
import BulkView from '../BulkView'
import styles from './Content.pcss'
import Media from './Media'
import { SuggestedTexts } from './SuggestedTexts'

const ARTICLE_CAPTION_CODES: CaptionPlaceholder[] = ['title', 'description', 'link']
const GENERIC_CAPTION_CODES: CaptionPlaceholder[] = ['caption', 'credit']

export function Content(props: { activeView: 'default' | 'bulk' }) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const genericStatus = useSelector(genericPostSelector)
  const composerKey = useSelector(composerResetKeySelector)
  const link = useSelector(composerLinkSelector)
  const content = useSelector(composerContentItemSelector)
  const imageUrls = useSelector(composerImageUrlsSelector)
  const videoUrl = useSelector(composerVideoUrlSelector)
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const multiPosts = useSelector(visibleMultiPostsSelector)
  const [showAtWarning, setShowAtWarning] = useState(false)
  const [captionCodes, setCaptionCodes] = useState<CaptionPlaceholder[] | undefined>(undefined)
  const isDefaultView = props.activeView === 'default'

  const linkRef = useRef(link)
  const getLinkDetails$ = useRef<Subject<string>>(undefined)
  const skipLinkFetch = useRef<boolean>(undefined)
  const [mainContentRef, mainContentRect] = useElementSize<HTMLDivElement>()
  const sideContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = mainContentRect.height
    if (h && sideContentRef.current) {
      sideContentRef.current.style.height = `${h}px`
    }
  }, [mainContentRect.height])

  useEffect(() => {
    if (props.activeView !== 'bulk') {
      setCaptionCodes(undefined)
      return
    }
    let withArticles = false
    let withPhotoContentItems = false
    for (const post of multiPosts) {
      const postContentType = post.content?.type
      if (postContentType === ARTICLE_TYPE || post.article) {
        withArticles = true
      } else if (postContentType === PHOTO_TYPE) {
        withPhotoContentItems = true
      }
    }
    if (withArticles) {
      setCaptionCodes(ARTICLE_CAPTION_CODES.concat(GENERIC_CAPTION_CODES))
    } else if (withPhotoContentItems) {
      setCaptionCodes(GENERIC_CAPTION_CODES)
    } else {
      setCaptionCodes(undefined)
    }
  }, [props.activeView, multiPosts])

  useEffect(() => {
    skipLinkFetch.current = (content && content?.type !== ARTICLE_TYPE) || imageUrls.length > 0 || Boolean(videoUrl)
  }, [content, imageUrls, videoUrl])

  useEffect(() => {
    getLinkDetails$.current = new Subject()
    const sub = getLinkDetails$.current.pipe(
      switchMap((url: string) => from(dispatch(getLinkDetails({ url })).unwrap()))
    )
      .subscribe()
    return () => {
      sub.unsubscribe()
    }
  }, [dispatch])

  const onChange = (text: string, html: string) => {
    if (html.indexOf('@') !== -1) {
      setShowAtWarning(true)
    } else {
      setShowAtWarning(false)
    }
    const url = extractUrlsFromText(text).pop()
    const lastFetchedUrl = linkRef.current ? linkRef.current.url : ''
    if (url && url !== lastFetchedUrl) {
      const validUrl = url.toLowerCase().startsWith('http') ? url : `http://${url}`
      // NOTE: Only fetch link details if no content item is added to the composer
      if (!skipLinkFetch.current) {
        getLinkDetails$.current?.next(validUrl)
      }
    }
    dispatch(setComposerStatus({ network: COMPOSER_STATUS_KEY_GENERIC, text, html }))
  }

  return (
    <Accordion
      title={<FormattedMessage id="composer.titles.content" />}
      contentClassName={isDefaultView ? undefined : styles['bulk-content']}
      order="2"
      id="composer-content"
    >
      {(isDefaultView || multiPostsCount > 0) && (
        <Fragment>
          <div className={styles.section}>
            <div className={styles.layout}>
              <div className={styles.left} ref={mainContentRef}>
                <div className={styles['text-box']}>
                  <TextEditor
                    network={COMPOSER_STATUS_KEY_GENERIC}
                    inputClassName={styles.textarea}
                    containerClassName={`${styles['textarea-container']} ${captionCodes && captionCodes.length > 0 ? styles.bulk : ''}`}
                    withEmojis
                    withGenerateText
                    templateStrings={captionCodes}
                    initialHTML={genericStatus.status.html}
                    key={composerKey}
                    placeholder={intl.formatMessage({ id: 'composer.status-placeholder-content' })}
                    id="composer-text-editor-generic"
                    onChange={onChange}
                  />
                </div>
                {isDefaultView && (
                  <div className={`${styles['section-bottom']}`}>
                    {showAtWarning && (
                      <div className={styles['hints-box']}>
                        <div><Icon path={mdiAlertCircleOutline} className={styles['icon-err']} size="28px" /></div>
                        <span className={styles.hints}>
                          <FormattedMessage id="composer.sections.content.at-warning" />
                        </span>
                      </div>
                    )}
                    <div className={styles.actions}>
                      <Media />
                    </div>
                  </div>
                )}
              </div>

              <div ref={sideContentRef} className={`${styles.right} ${isDefaultView ? styles.exp : ''}`}>
                <SuggestedTexts />
              </div>
            </div>
          </div>
        </Fragment>
      )}
      {!isDefaultView && <BulkView />}
    </Accordion>
  )
}

export default Content
